import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { showAddOrUpdateInternshipSiteState } from "@/atoms";
import CustomInput from "@/components/common/inputs/CustomInput";
import MainModal from "@/components/modals/MainModal";
import useAuth from "@/hooks/useAuth";
import { useUserCouncil } from "@/hooks/useUserCouncil";
import { useGetAllCouncils } from "@/services/councils";
import {
  useCreateInternshipSite,
  useUpdateInternshipSite,
  useCheckInternshipSiteExists,
} from "@/services/internship-sites";
import {
  InternshipSiteRequest,
  InternshipSiteUpdateRequest,
  EInternshipSiteStatus,
} from "@/types/internship-sites.types";
import { Select, Button, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/useDebounce";

const AddUpdateInternshipSite = () => {
  const [show, setShow] = useRecoilState(showAddOrUpdateInternshipSiteState);
  const { user } = useAuth();
  const council = useUserCouncil();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    status: EInternshipSiteStatus;
    councilId: string | null;
  }>({
    title: "",
    description: "",
    status: EInternshipSiteStatus.ACTIVE,
    councilId: null,
  });

  const [errors, setErrors] = useState<{
    title?: string;
    description?: string;
    status?: string;
    councilId?: string;
  }>({});

  const { data: councilsData } = useGetAllCouncils();
  const councils = councilsData?.data || [];

  const createMutation = useCreateInternshipSite();
  const updateMutation = useUpdateInternshipSite();

  // Debounce title for uniqueness check
  const debouncedTitle = useDebounce(formData.title, 500);

  // Check title uniqueness (only for create mode and when title and councilId are provided)
  const { data: titleExists, isLoading: checkingTitle } =
    useCheckInternshipSiteExists(
      debouncedTitle,
      formData.councilId || "",
      show?.action === "add" && !!debouncedTitle && !!formData.councilId
    );

  // Status options
  const statusOptions = [
    { value: EInternshipSiteStatus.ACTIVE, label: "Active" },
    { value: EInternshipSiteStatus.SUSPENDED, label: "Suspended" },
    { value: EInternshipSiteStatus.TERMINATED, label: "Terminated" },
  ];

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    } else if (formData.title.length > 255) {
      newErrors.title = "Title must not exceed 255 characters";
    } else if (
      show?.action === "add" &&
      titleExists &&
      formData.councilId &&
      debouncedTitle === formData.title
    ) {
      newErrors.title = "This title already exists for the selected council";
    }

    if (formData.description.length > 1000) {
      newErrors.description = "Description must not exceed 1000 characters";
    }

    if (!formData.status) {
      newErrors.status = "Status is required";
    }

    if (!formData.councilId) {
      newErrors.councilId = "Council is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (show?.action === "add") {
      const requestData: InternshipSiteRequest = {
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        status: formData.status,
        councilId: formData.councilId!,
      };

      createMutation.mutate(requestData, {
        onSuccess: () => {
          notifications.show({
            title: "Success",
            message: "Internship site created successfully",
            color: "green",
          });
          queryClient.invalidateQueries({
            queryKey: ["internship-sites"],
          });
          setShow(null);
          resetForm();
        },
        onError: (error: any) => {
          const errorMessage =
            error.response?.data?.message ||
            error.response?.data?.errors?.[0] ||
            "Failed to create internship site";
          notifications.show({
            title: "Error",
            message: errorMessage,
            color: "red",
          });
        },
      });
    } else if (show?.action === "edit" && show.internshipSite) {
      const updateData: InternshipSiteUpdateRequest = {};

      if (formData.title.trim() !== show.internshipSite.title) {
        updateData.title = formData.title.trim();
      }
      if (formData.description.trim() !== (show.internshipSite.description || "")) {
        updateData.description = formData.description.trim() || undefined;
      }
      if (formData.status !== show.internshipSite.status) {
        updateData.status = formData.status;
      }
      if (formData.councilId !== show.internshipSite.councilId) {
        updateData.councilId = formData.councilId!;
      }

      // Only update if there are changes
      if (Object.keys(updateData).length > 0) {
        updateMutation.mutate(
          { id: show.internshipSite.id, data: updateData },
          {
            onSuccess: () => {
              notifications.show({
                title: "Success",
                message: "Internship site updated successfully",
                color: "green",
              });
              queryClient.invalidateQueries({
                queryKey: ["internship-sites"],
              });
              setShow(null);
              resetForm();
            },
            onError: (error: any) => {
              const errorMessage =
                error.response?.data?.message ||
                error.response?.data?.errors?.[0] ||
                "Failed to update internship site";
              notifications.show({
                title: "Error",
                message: errorMessage,
                color: "red",
              });
            },
          }
        );
      } else {
        // No changes, just close the modal
        setShow(null);
        resetForm();
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      status: EInternshipSiteStatus.ACTIVE,
      councilId: null,
    });
    setErrors({});
  };

  // Initialize form data when modal opens
  useEffect(() => {
    if (show?.show) {
      if (show.action === "edit" && show.internshipSite) {
        setFormData({
          title: show.internshipSite.title,
          description: show.internshipSite.description || "",
          status: show.internshipSite.status,
          councilId: show.internshipSite.councilId,
        });
      } else if (show.action === "add") {
        // Set default council for ADMINCOUNCIL users
        const defaultCouncilId =
          user?.roles?.[0]?.roleName === "ADMINCOUNCIL"
            ? council?.id || null
            : null;
        setFormData({
          title: "",
          description: "",
          status: EInternshipSiteStatus.ACTIVE,
          councilId: defaultCouncilId,
        });
      }
      setErrors({});
    }
  }, [show, user]);

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <MainModal
      open={Boolean(show?.show)}
      onClose={() => {
        setShow(null);
        resetForm();
      }}
      title={show?.action === "add" ? "Add Internship Site" : "Update Internship Site"}
      description={
        show?.action === "add"
          ? "Fill in the details to create a new internship site."
          : "Update the internship site details as needed."
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-4">
        <CustomInput
          type="text"
          name="title"
          value={formData.title}
          onChange={(e) =>
            setFormData({ ...formData, title: e.target.value })
          }
          label="Title"
          placeholder="Enter internship site title"
          required
          alwaysFocused
          error={errors.title}
          description={
            checkingTitle
              ? "Checking availability..."
              : titleExists && show?.action === "add"
              ? "This title already exists for the selected council"
              : undefined
          }
        />

        <CustomInput
          textarea
          rows={4}
          name="description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          label="Description"
          placeholder="Enter description (optional)"
          alwaysFocused
          error={errors.description}
          description="Maximum 1000 characters"
        />

        <CustomInput label="Status" alwaysFocused required>
          <Select
            value={formData.status}
            onChange={(val) =>
              setFormData({
                ...formData,
                status: (val as EInternshipSiteStatus) || EInternshipSiteStatus.ACTIVE,
              })
            }
            data={statusOptions.map((opt) => ({
              value: opt.value,
              label: opt.label,
            }))}
            placeholder="Select status"
            variant="unstyled"
            className="w-full"
            error={errors.status}
          />
        </CustomInput>

        <CustomInput
          label="Council"
          alwaysFocused
          required
          error={errors.councilId}
        >
          <Select
            value={formData.councilId || ""}
            onChange={(val) =>
              setFormData({ ...formData, councilId: val || null })
            }
            data={councils.map((council) => ({
              value: council.id,
              label: council.councilName,
            }))}
            placeholder="Select council"
            variant="unstyled"
            className="w-full"
            disabled={
              user?.roles?.[0]?.roleName === "ADMINCOUNCIL" ||
              show?.action === "edit"
            }
          />
        </CustomInput>

        <div className="flex justify-end gap-2 mt-4">
          <Button
            variant="light"
            onClick={() => {
              setShow(null);
              resetForm();
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isPending}>
            {show?.action === "add" ? "Create" : "Update"}
          </Button>
        </div>
      </form>
    </MainModal>
  );
};

export default AddUpdateInternshipSite;
